import { call, put, takeLatest } from 'redux-saga/effects';
import {
  CASE_FETCH_CRAWL_SUMMARY_BEGIN,
  CASE_FETCH_CRAWL_SUMMARY_SUCCESS,
  CASE_FETCH_CRAWL_SUMMARY_FAILURE,
  CASE_FETCH_CRAWL_SUMMARY_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const fetchCrawlSummary = createPromiseAction(CASE_FETCH_CRAWL_SUMMARY_BEGIN);

export function dismissFetchCrawlSummaryFeedback() {
  return {
    type: CASE_FETCH_CRAWL_SUMMARY_DISMISS_FEEDBACK,
  };
}

export function* doFetchCrawlSummary(action) {
  const {
    payload: { caseId },
  } = action;
  const res = yield call(api.get, `/cases/${caseId}/caseintel/crawl-summary`);

  if (res && res.error) {
    // "No crawl yet" errors — treat as null (no crawl), not a failure.
    // The API returns a body like { error: "No crawl summary found...", hint: "No crawl has been run yet..." }
    const errorStr =
      typeof res.error === 'string' ? res.error : res.error?.error || res.error?.hint || '';
    const isNoCrawl =
      errorStr.toLowerCase().includes('no crawl') || errorStr.toLowerCase().includes('not found');

    if (isNoCrawl) {
      const hint =
        (typeof res.error === 'object' && res.error?.hint) || 'No crawl has been run yet.';
      yield put({
        type: CASE_FETCH_CRAWL_SUMMARY_SUCCESS,
        data: null,
        crawlSummaryHint: hint,
      });
      return yield call(resolvePromiseAction, action, null);
    }

    yield put({
      type: CASE_FETCH_CRAWL_SUMMARY_FAILURE,
      feedback: {
        message: 'feedback.fetchCrawlSummaryFailure',
        error: res.error,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_FETCH_CRAWL_SUMMARY_SUCCESS,
    data: res,
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchFetchCrawlSummary() {
  yield takeLatest(fetchCrawlSummary, withCurrentCaseId(doFetchCrawlSummary));
}

export function useFetchCrawlSummary() {
  const dispatch = useDispatch();

  const { crawlSummary, crawlSummaryPending, crawlSummaryFeedback, crawlSummaryHint } = useSelector(
    (state) => ({
      crawlSummary: state.case.crawlSummary,
      crawlSummaryPending: state.case.crawlSummaryPending,
      crawlSummaryFeedback: state.case.crawlSummaryFeedback,
      crawlSummaryHint: state.case.crawlSummaryHint,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchCrawlSummary(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchCrawlSummaryFeedback());
  }, [dispatch]);

  return {
    crawlSummary,
    fetchCrawlSummary: boundAction,
    crawlSummaryPending,
    crawlSummaryFeedback,
    crawlSummaryHint,
    dismissFetchCrawlSummaryFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_CRAWL_SUMMARY_BEGIN + '.TRIGGER':
      return {
        ...state,
        crawlSummaryPending: true,
        crawlSummaryFeedback: null,
      };

    case CASE_FETCH_CRAWL_SUMMARY_SUCCESS:
      return {
        ...state,
        crawlSummaryPending: false,
        crawlSummaryFeedback: action.feedback,
        crawlSummary: action.data,
        crawlSummaryHint: action.crawlSummaryHint || null,
      };

    case CASE_FETCH_CRAWL_SUMMARY_FAILURE:
      return {
        ...state,
        crawlSummaryPending: false,
        crawlSummaryFeedback: action.feedback,
      };

    case CASE_FETCH_CRAWL_SUMMARY_DISMISS_FEEDBACK:
      return {
        ...state,
        crawlSummaryFeedback: null,
      };

    default:
      return state;
  }
}
